/**
 * ===========================================================================
 *  DIRECTOR — who is on the field, and where
 * ===========================================================================
 *
 * The reference plate is explicit about this: a wide Dynasty Warriors frame
 * holds 60–200 bodies **in clumps of 8–30**, never an even scatter. Three
 * separate critics have already flagged the even scatter as this build's most
 * visible failure, so the director never places a unit at a random point of
 * the map. Every unit is born into a *knot*:
 *
 *   - a garrison knot inside or around a base,
 *   - a marching column on a road,
 *   - or a melee knot on the front line, paired with an opposing knot so the
 *     two are already fighting when the player first sees them.
 *
 * On top of the authored battle it streams: it spawns just outside the
 * player's view, despawns far behind him, holds `CFG.q.crowdMax`, and scales
 * how hard the enemy presses to how well the player is doing.
 *
 *   world.director = { spawnRing, spawnSquad, spawnKnot, spawnOfficer,
 *                      pressure, population, cap, setCap, hotspots, stats }
 */
import { ORDER } from '../core/systems.js';
import { KIND, TEAM, ST, FLAG } from '../core/entities.js';
import { CFG } from '../core/config.js';
import { clamp, clamp01 } from '../core/mathx.js';
import { ORDERS, FORM, ROLE, AIF } from './store.js';
import { RIVER, ROADS, SITES, BRIDGE, HAS_BRIDGE, OPENING } from '../world/layout.js';
import { WALLS, clear as wallClear } from '../world/walls.js';

/**
 * `Math.hypot` is NOT a cheap intrinsic in V8: it is a variadic builtin that
 * boxes its arguments, and it measured at ~155 bytes of garbage per call
 * against ~0 for `Math.sqrt`. This module calls it once or more per unit per
 * fixed step, which is the whole of the AI's allocation rate and therefore the
 * whole of `aiCpuPeak` — the mean was never the problem, the scavenges landing
 * inside the unit loop were. Same arithmetic, no allocation.
 */
const hyp = (x, z) => Math.sqrt(x * x + z * z);


const _rq = { d: 0, s: 0, cx: 0, cz: 0, tx: 1, tz: 0 };
const _opts = { form: 0, order: 0, gx: 0, gz: 0, fx: 0, fz: 1, speed: 2.1, spacing: 1.85, road: 0, baseId: -1, escort: -1 };

/** Per-kind stat block. Everything the crowd renderer and combat need. */
function dress(w, id, kind, team) {
  const e = w.entities, r = w.rng;
  e.radius[id] = 0.42; e.height[id] = 1.75; e.mass[id] = 1;
  e.variant[id] = (r.next() * 65535) | 0;
  e.morale[id] = 0.52 + r.next() * 0.34;
  e.state[id] = ST.IDLE;
  switch (kind) {
    case KIND.ARCHER:
      e.hp[id] = e.maxHp[id] = 34; e.scale[id] = 0.94 + r.next() * 0.08;
      e.setFlag(id, FLAG.RANGED, true); break;
    case KIND.SERGEANT:
      e.hp[id] = e.maxHp[id] = 165; e.scale[id] = 1.04 + r.next() * 0.06;
      e.radius[id] = 0.48; e.mass[id] = 1.5;
      e.setFlag(id, FLAG.CAPTAIN | FLAG.ELITE, true); break;
    case KIND.OFFICER: case KIND.ALLY_OFFICER:
      e.hp[id] = e.maxHp[id] = 900; e.scale[id] = 1.10 + r.next() * 0.05;
      e.radius[id] = 0.5; e.mass[id] = 2.2; e.maxPoise[id] = e.poise[id] = 60;
      e.setFlag(id, FLAG.ELITE, true); break;
    case KIND.GENERAL:
      e.hp[id] = e.maxHp[id] = 1900; e.scale[id] = 1.18 + r.next() * 0.05;
      e.radius[id] = 0.56; e.mass[id] = 3.2; e.maxPoise[id] = e.poise[id] = 90;
      e.setFlag(id, FLAG.ELITE | FLAG.BOSS, true); break;
    default:
      e.hp[id] = e.maxHp[id] = 44 + r.next() * 20;
      e.scale[id] = 0.93 + r.next() * 0.13;
      break;
  }
  e.y[id] = w.terrain ? w.terrain.heightAt(e.x[id], e.z[id]) : 0;
}

export function createDirector() {
  let seeded = false;
  let cap = 600;
  let streamT = 0, auditT = 0;
  let hot = [];                       // authored knot sites
  const stats = { spawned: 0, despawned: 0, waves: 0, knots: 0, near: 0, target: 0 };
  let pressure = 0.5, killsAt = 0, killT = 0, killRate = 0;
  let api = null;

  /* ------------------------------------------------------------ placing */

  function placeOk(w, x, z) {
    if (x < -246 || x > 246 || z < -246 || z > 246) return false;
    RIVER.query(x, z, _rq);
    if (_rq.d < 12) return false;                       // nobody fords the Brantas
    if (w.terrain && w.terrain.slopeAt(x, z) > 0.6) return false;
    // inside the curtain, and never born standing in masonry
    if (WALLS.n && !wallClear(x, z, 1.2)) return false;
    return true;
  }

  /** Deterministic gaussian scatter inside a knot, with a few retries. */
  function scatter(w, cx, cz, sd, out) {
    const r = w.rng;
    for (let i = 0; i < 6; i++) {
      const x = cx + r.gauss() * sd, z = cz + r.gauss() * sd;
      // a knot is one body of men: none of it scattered over a wall
      if (WALLS.n && WALLS.blocked(cx, cz, x, z, 0)) continue;
      if (placeOk(w, x, z)) { out.x = x; out.z = z; return true; }
    }
    out.x = cx; out.z = cz;
    return placeOk(w, cx, cz);
  }
  const _p = { x: 0, z: 0 };

  /* ------------------------------------------------------------ spawning */

  function spawnUnit(w, kind, team, x, z, squad, role) {
    const e = w.entities;
    if (e.stats.total >= cap + 40) return -1;
    const y = w.terrain ? w.terrain.heightAt(x, z) : 0;
    const id = e.spawn(kind, team, x, y, z);
    if (id < 0) return -1;
    dress(w, id, kind, team);
    w.ai?.enlist?.(id, squad, role);
    stats.spawned++;
    return id;
  }

  /**
   * One knot: a squad of `n` bodies clustered around (cx,cz) with a formation
   * and an order. This is the only way units ever enter the world.
   */
  function spawnKnot(w, team, cx, cz, n, o = null) {
    const ai = w.ai;
    if (!ai) return -1;
    const form = o?.form ?? FORM.BLOB;
    _opts.form = form;
    _opts.order = o?.order ?? ORDERS.HOLD;
    _opts.gx = o?.gx ?? cx; _opts.gz = o?.gz ?? cz;
    _opts.fx = o?.fx ?? 0; _opts.fz = o?.fz ?? 1;
    _opts.speed = o?.speed ?? 2.0;
    _opts.spacing = o?.spacing ?? (form === FORM.BLOB ? 1.5 : 1.9);
    _opts.road = o?.road ?? 0;
    _opts.baseId = o?.baseId ?? -1;
    _opts.escort = -1;
    const sq = ai.newSquad(team, cx, cz, _opts);
    if (sq < 0) return -1;
    const sd = o?.sd ?? (1.7 + n * 0.09);
    const archers = o?.archers ?? 0;
    const enemy = team === TEAM.ENEMY;
    let made = 0;
    for (let i = 0; i < n; i++) {
      if (!scatter(w, cx, cz, sd, _p)) continue;
      let kind = enemy ? KIND.GRUNT : KIND.ALLY;
      let role = ROLE.GRUNT;
      if (i === 0 && n >= 7) { kind = KIND.SERGEANT; role = ROLE.CAPTAIN; }
      else if (archers > 0 && i > 0 && i <= archers) { kind = KIND.ARCHER; role = ROLE.ARCHER; }
      const id = spawnUnit(w, kind, team, _p.x, _p.z, sq, role);
      if (id < 0) break;
      if (i === 0) w.ai.squads.leader[sq] = id;
      made++;
    }
    if (made === 0) { w.ai.squads.disband(sq); return -1; }
    stats.knots++;
    return sq;
  }

  /** A named officer plus his retinue. */
  function spawnOfficer(w, team, cx, cz, n = 10, o = null) {
    const enemy = team === TEAM.ENEMY;
    const sq = spawnKnot(w, team, cx, cz, n, o || { form: FORM.WEDGE, order: ORDERS.HOLD });
    if (sq < 0) return -1;
    const id = spawnUnit(w, o?.general ? KIND.GENERAL : (enemy ? KIND.OFFICER : KIND.ALLY_OFFICER),
      team, cx, cz, sq, ROLE.OFFICER);
    if (id >= 0) {
      w.ai.squads.leader[sq] = id;
      w.ai.squads.escort[sq] = id;
      w.ai.registerOfficer(id, o?.name);
    }
    return id;
  }

  /* ---------------------------------------------------------- the battle */

  /** Authored opening state of the battle. Runs once, on the first step. */
  function seed(w) {
    const B = w.bases?.list || [];
    hot = [];

    // ---- 1. base garrisons ------------------------------------------
    for (const b of B) {
      if (b.team === TEAM.NEUTRAL) continue;
      const heavy = b.type === 'fort';
      const spawns = b.spawns && b.spawns.length ? b.spawns : null;
      const knots = heavy ? 3 : 2;
      for (let k = 0; k < knots; k++) {
        const s = spawns ? spawns[(k * 2) % spawns.length] : null;
        const a = (k / knots) * 6.2832 + b.x * 0.01;
        const cx = s ? s.x : b.x + Math.sin(a) * b.radius * 0.55;
        const cz = s ? s.z : b.z + Math.cos(a) * b.radius * 0.55;
        const n = heavy ? 13 + ((k * 7) % 6) : 9 + ((k * 5) % 5);
        const fx = b.x - cx, fz = b.z - cz, fl = hyp(fx, fz) || 1;
        spawnKnot(w, b.team, cx, cz, n, {
          form: k === 0 ? FORM.LINE : FORM.BLOB, order: ORDERS.HOLD,
          fx: -fx / fl, fz: -fz / fl, spacing: 1.9, archers: k === 1 ? 3 : 0, baseId: b.id, sd: 2.6,
        });
        hot.push({ x: cx, z: cz, r: 16, team: b.team, quota: n, kind: 'garrison' });
      }
      // one officer holds every real base
      spawnOfficer(w, b.team, b.x + Math.sin(b.z) * 6, b.z + Math.cos(b.x) * 6,
        heavy ? 12 : 8, { form: FORM.WEDGE, general: heavy && b.team === TEAM.ENEMY, order: ORDERS.HOLD });
      hot.push({ x: b.x, z: b.z, r: 22, team: b.team, quota: 16, kind: 'base' });
    }

    // ---- 2. the front line -------------------------------------------
    // Paired knots straddling road A near the player's start, already locked
    // together. This is the battle the player walks into.
    // x, z of the ENEMY knot; the allied knot forms up 7.5 m behind it on the
    // player's side. The band runs across the player's spawn (0,30) so the
    // default gameplay framing has a battle in it, near enough to walk into
    // and far enough that he is not swarmed on frame one.
    // The ground's own opening (arenas.js `opening`). These three tables were
    // Lembah Brantas literals in this file, and on a walled arena they put
    // knots against masonry, where scatter() rejects the men one by one: the
    // kraton's capture gate came out at 464 live against a 500 floor.
    const FRONT = OPENING.front;
    for (let i = 0; i < FRONT.length; i++) {
      const [fx, fz, ne, na] = FRONT[i];
      const ax = fx + (i % 2 ? 3 : -3), az = fz + 7.5;
      spawnKnot(w, TEAM.ENEMY, fx, fz - 3.5, ne, {
        form: FORM.LINE, order: ORDERS.ADVANCE, gx: ax, gz: az, fx: 0, fz: 1, sd: 2.9, spacing: 1.8,
      });
      spawnKnot(w, TEAM.PLAYER, ax, az, na, {
        form: FORM.LINE, order: ORDERS.ADVANCE, gx: fx, gz: fz - 3.5, fx: 0, fz: -1, sd: 2.7, spacing: 1.8,
        archers: i === 1 ? 2 : 0,
      });
      hot.push({ x: fx, z: fz + 2, r: 18, team: -1, quota: ne + na, kind: 'front' });
    }
    // a named enemy officer leading the push down the road, and an ally one
    for (const o of OPENING.officers) {
      spawnOfficer(w, o[0] === 'enemy' ? TEAM.ENEMY : TEAM.PLAYER, o[1], o[2], o[3],
        { form: FORM.WEDGE, order: ORDERS.ADVANCE, gx: o[4], gz: o[5] });
    }

    // ---- 3. marching columns on the roads -----------------------------
    const COLS = OPENING.cols;
    for (const [side, sx, sz, gx, gz, n] of COLS) {
      const team = side === 'enemy' ? TEAM.ENEMY : TEAM.PLAYER;
      const fx = gx - sx, fz = gz - sz, fl = hyp(fx, fz) || 1;
      spawnKnot(w, team, sx, sz, n, {
        form: FORM.COLUMN, order: ORDERS.ADVANCE, gx, gz, fx: fx / fl, fz: fz / fl,
        speed: 2.4, spacing: 1.8, road: 1, sd: 1.6,
      });
      hot.push({ x: sx, z: sz, r: 14, team, quota: n, kind: 'road' });
    }

    // ---- 4. the crossing ----------------------------------------------
    // Both banks are ordered ACROSS, in column, at the abutment on the far
    // side. Held, they were twelve Kidul on the west bank and eight Wilwatikta
    // standing on the east, 35 m apart with a stone bridge between them and
    // neither side ever ordered onto it. A crossing is a thing two armies
    // fight over; the column formation is what keeps them on the 9 m deck.
    // Only an arena with a bridge has a crossing to fight over.
    if (HAS_BRIDGE) {
    spawnKnot(w, TEAM.ENEMY, BRIDGE.ax - 7, BRIDGE.az + 2, 14, {
      form: FORM.COLUMN, order: ORDERS.ADVANCE, gx: BRIDGE.bx, gz: BRIDGE.bz,
      fx: 1, fz: 0, sd: 2.6, speed: 2.9, spacing: 1.75,
    });
    spawnKnot(w, TEAM.PLAYER, BRIDGE.bx + 8, BRIDGE.bz - 2, 12, {
      form: FORM.COLUMN, order: ORDERS.ADVANCE, gx: BRIDGE.ax, gz: BRIDGE.az,
      fx: -1, fz: 0, sd: 2.6, speed: 2.9, spacing: 1.75,
    });
    hot.push({ x: BRIDGE.ax - 7, z: BRIDGE.az, r: 16, team: TEAM.ENEMY, quota: 14, kind: 'cross' });
    hot.push({ x: BRIDGE.bx + 8, z: BRIDGE.bz, r: 16, team: TEAM.PLAYER, quota: 12, kind: 'cross' });
    }

    // ---- 4a. the gate crossings ---------------------------------------
    // The walled arenas' version of the bridge above, seeded at t = 0 just as
    // the bridge is: a Kidul column on the inner side of a gate and an allied
    // one on the outer, each ordered through the passage at the other. The
    // capture gate's density scenarios photograph ~2 s after boot, so what is
    // on the field is what the SEED put there — refill-only hotspots moved the
    // longer combo scenario and left `crowd` exactly where it was.
    for (const c of OPENING.crossings || []) {
      const [ax, az, bx, bz, ne, na] = c;
      let fx = bx - ax, fz = bz - az; const fl = hyp(fx, fz) || 1; fx /= fl; fz /= fl;
      spawnKnot(w, TEAM.ENEMY, ax, az, ne, {
        form: FORM.COLUMN, order: ORDERS.ADVANCE, gx: bx, gz: bz, fx, fz, sd: 2.4, speed: 2.9, spacing: 1.75,
      });
      spawnKnot(w, TEAM.PLAYER, bx, bz, na, {
        form: FORM.COLUMN, order: ORDERS.ADVANCE, gx: ax, gz: az, fx: -fx, fz: -fz, sd: 2.4, speed: 2.9, spacing: 1.75,
      });
      hot.push({ x: ax, z: az, r: 16, team: TEAM.ENEMY, quota: ne, kind: 'gate' });
      hot.push({ x: bx, z: bz, r: 16, team: TEAM.PLAYER, quota: na, kind: 'gate' });
    }

    // ---- 4b. authored hotspots -----------------------------------------
    // A walled arena's crossing is its gates, the way the Brantas's is its
    // bridge, and a raid's is its treeline: the streamer keeps a knot at each
    // one the arena names. Contested (team -1), so it refills the losing side.
    for (const q of OPENING.hotspots || []) {
      hot.push({ x: q[0], z: q[1], r: 14, team: -1, quota: q[2], kind: q[3] || 'gate' });
    }

    // ---- 5. road hotspots for the streamer ----------------------------
    for (const rd of ROADS) {
      const L = rd.line.len;
      for (let s = 40; s < L - 40; s += 78) {
        rd.line.at(s, _rq);
        hot.push({ x: _rq.x, z: _rq.z, r: 15, team: -1, quota: 10, kind: 'road' });
      }
    }
    seeded = true;
  }

  /* --------------------------------------------------------- the streamer */

  /** Is (x,z) inside the player's forward view? */
  function inView(w, x, z) {
    const e = w.entities, pid = w.playerId;
    if (pid < 0) return false;
    const dx = x - e.x[pid], dz = z - e.z[pid];
    const d = hyp(dx, dz);
    if (d > 150) return false;
    if (d < 26) return true;
    const yaw = w.camrig ? w.camrig.yaw : e.yaw[pid];
    const fx = Math.sin(yaw), fz = Math.cos(yaw);
    return (fx * dx + fz * dz) / d > 0.45;             // ~±63°, a wide FOV plus slack
  }

  function cullFar(w, need) {
    const e = w.entities, pid = w.playerId;
    if (pid < 0) return 0;
    const px = e.x[pid], pz = e.z[pid];
    let done = 0;
    for (let i = e.liveN - 1; i >= 0 && done < need; i--) {
      const id = e.live[i];
      if (id === pid || !e.alive[id]) continue;
      if (e.has(id, FLAG.ELITE)) continue;             // never despawn a named man
      const dx = e.x[id] - px, dz = e.z[id] - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 < 110 * 110) continue;
      if (inView(w, e.x[id], e.z[id])) continue;
      w.ai?.discharge?.(id);
      e.release(id);
      done++; stats.despawned++;
    }
    return done;
  }

  /** Reinforcements: a squad arrives from off-screen and heads for the fight. */
  function sendWave(w, tx, tz, team, n) {
    const e = w.entities, pid = w.playerId;
    if (pid < 0) return -1;
    const px = e.x[pid], pz = e.z[pid];
    let bx = 0, bz = 0, found = false;
    // `inView` counts everything inside a 63 deg cone out to 150 m, so a wave
    // could only ever be born BEHIND the player — and then had to chase him.
    // Measured over a whole battle: 82 % of the hero's swings hit nothing
    // because the reinforcements he had outrun never caught up. Beyond ~78 m a
    // knot is at crowd LOD2 and reads as a distant smudge, so letting waves
    // land ahead of him at that range costs no visible pop and keeps the fight
    // in front of him, which is where a Warriors battle lives.
    for (let k = 0; k < 10; k++) {
      const a = w.rng.next() * 6.2832;
      const r = 52 + w.rng.next() * 34;
      const x = px + Math.sin(a) * r, z = pz + Math.cos(a) * r;
      if (!placeOk(w, x, z)) continue;
      const d2 = (x - px) * (x - px) + (z - pz) * (z - pz);
      if (d2 < 78 * 78 && inView(w, x, z)) continue;   // never pop in close, on camera
      bx = x; bz = z; found = true; break;
    }
    if (!found) return -1;
    const fx = tx - bx, fz = tz - bz, fl = hyp(fx, fz) || 1;
    stats.waves++;
    return spawnKnot(w, team, bx, bz, n, {
      form: FORM.WEDGE, order: ORDERS.ADVANCE, gx: tx, gz: tz,
      fx: fx / fl, fz: fz / fl, speed: 4.6, spacing: 1.85, sd: 2.2,
    });
  }

  /** Keep the far field populated so a wide shot is never empty. */
  function refill(w) {
    const e = w.entities;
    if (hot.length === 0) return;
    // Walk the hotspot list a few at a time — never all of them in one step.
    // Two knots per tick is what keeps the field at its cap while the
    // background war is killing 3–6 men a second somewhere on the map.
    let made = 0;
    for (let k = 0; k < 8 && made < 2; k++) {
      const h = hot[(refill.i = (refill.i + 1) % hot.length)];
      if (inView(w, h.x, h.z)) continue;
      const have = e.countIn(h.x, h.z, h.r, -1);
      if (have >= h.quota * 0.8) continue;
      const road = h.kind === 'road';

      // A contested hotspot refills the side that is LOSING it, and if it has
      // emptied entirely it refills both sides at once. A "front" that respawns
      // two red knots and no blue one is not a front, it is a car park — and
      // with the whole map now marching at whatever it can find, a hotspot
      // going one-sided was the main way the war thinned itself out.
      let team = h.team;
      let pair = 0;
      if (team < 0) {
        const nE = e.countIn(h.x, h.z, h.r, TEAM.ENEMY);
        const nP = e.countIn(h.x, h.z, h.r, TEAM.PLAYER);
        if (nE + nP < 4 && !road) { pair = 1; team = TEAM.ENEMY; }
        else team = nE <= nP ? TEAM.ENEMY : TEAM.PLAYER;
      }
      const n = Math.min(14, Math.max(6, (h.quota - have) | 0));
      if (e.stats.total + n * (pair ? 2 : 1) > cap) return;

      // A refilled road knot used to be handed gx/gz equal to its OWN spawn
      // point, so squad.js flipped it to HOLD on the first update and the
      // "marching column" stood on the road for the rest of the battle.
      // Give it somewhere down the road to actually be.
      let gx = h.x, gz = h.z, fx = 0, fz = 1;
      // 'cross' hotspots are only ever created by the bridge seeding above, so
      // without a bridge this is already unreachable — guarded anyway so that
      // stays true by construction rather than by coincidence.
      const cross = h.kind === 'cross' && HAS_BRIDGE;
      if (cross) {
        // the far abutment: a knot respawned at the crossing is a knot that
        // has to fight its way over it
        const west = h.x < BRIDGE.x;
        gx = west ? BRIDGE.bx : BRIDGE.ax; gz = west ? BRIDGE.bz : BRIDGE.az;
        fx = gx - h.x; fz = gz - h.z;
      } else if (road && roadGoal(w, h.x, h.z, team)) { gx = _rg.x; gz = _rg.z; fx = gx - h.x; fz = gz - h.z; }
      const fl = hyp(fx,fz) || 1;
      spawnKnot(w, team, h.x, h.z, n, {
        form: (road || cross) ? FORM.COLUMN : FORM.BLOB,
        order: (road || cross) ? ORDERS.ADVANCE : ORDERS.HOLD,
        gx, gz, fx: fx / fl, fz: fz / fl, speed: cross ? 2.9 : road ? 2.6 : 2.0,
        spacing: cross ? 1.75 : undefined,
        road: road ? 1 : 0, sd: 2.4,
      });
      made++;
      if (pair) {
        // the opposing knot, 13 m away and facing him
        const a = h.x * 0.031 + h.z * 0.017;
        const ox = Math.sin(a) * 13, oz = Math.cos(a) * 13;
        spawnKnot(w, TEAM.PLAYER, h.x + ox, h.z + oz, n, {
          form: FORM.LINE, order: ORDERS.ADVANCE, gx: h.x, gz: h.z,
          fx: -ox / 13, fz: -oz / 13, speed: 2.4, sd: 2.4,
        });
        made++;
      }
    }
  }
  refill.i = 0;

  const _rg = { x: 0, z: 0, tx: 0, tz: 0 };
  /**
   * Somewhere down the nearest road for a column to march to: whichever end
   * takes it closer to a site the other side still holds.
   */
  function roadGoal(w, x, z, team) {
    let best = null, bd = 1e9, bs = 0;
    for (let k = 0; k < ROADS.length; k++) {
      ROADS[k].line.query(x, z, _rq);
      if (_rq.d < bd) { bd = _rq.d; best = ROADS[k]; bs = _rq.s; }
    }
    if (!best || bd > 22) return false;
    const foe = team === TEAM.ENEMY ? TEAM.PLAYER : TEAM.ENEMY;
    // the two candidate ends of this leg
    let fx = 0, fz = 0, fd = 1e9;
    for (const s of SITES) {
      if (s.team !== foe) continue;
      const d = hyp(s.x - x, s.z - z);
      if (d < fd) { fd = d; fx = s.x; fz = s.z; }
    }
    const fwd = Math.min(best.line.len, bs + 86), back = Math.max(0, bs - 86);
    best.line.at(fwd, _rq); const dF = fd < 1e8 ? hyp(_rq.x - fx, _rq.z - fz) : 0;
    const ax = _rq.x, az = _rq.z;
    best.line.at(back, _rq); const dB = fd < 1e8 ? hyp(_rq.x - fx, _rq.z - fz) : 1;
    if (dF <= dB) { _rg.x = ax; _rg.z = az; } else { _rg.x = _rq.x; _rg.z = _rq.z; }
    return hyp(_rg.x - x, _rg.z - z) > 10;
  }

  /** Restore every battle-local pacing and streaming accumulator. */
  function resetBattle() {
    seeded = false;
    streamT = 0; auditT = 0;
    hot.length = 0; refill.i = 0;
    pressure = 0.5; killsAt = 0; killT = 0; killRate = 0;
    stats.spawned = 0; stats.despawned = 0; stats.waves = 0; stats.knots = 0;
    stats.near = 0; stats.target = 0;
    return api;
  }

  /* ================================================================ system */

  return {
    name: 'director', order: ORDER.MISSION,

    init(w) {
      // The crowd renderer will draw up to CFG.q.crowdMax, but the frame
      // budget (2.2 M triangles) is what actually binds: measured in ai_ring,
      // 507 live units in a dense close-up frame cost 2.25 M. 560 keeps the
      // contract's ">= 500 live" while leaving the renderer headroom.
      cap = Math.min(CFG.q.crowdMax || 600, 560);
      api = {
        /** Legacy/simple helper: a plain ring of grunts. Kept for scenarios. */
        spawnRing(n, team, cx, cz, r0, r1) {
          const ids = [];
          for (let i = 0; i < n; i++) {
            const a = w.rng.next() * 6.2832, r = r0 + w.rng.next() * (r1 - r0);
            const x = cx + Math.cos(a) * r, z = cz + Math.sin(a) * r;
            const id = spawnUnit(w, team === TEAM.ENEMY ? KIND.GRUNT : KIND.ALLY, team, x, z, -1, ROLE.GRUNT);
            if (id >= 0) ids.push(id);
          }
          return ids;
        },
        spawnKnot: (team, x, z, n, o) => spawnKnot(w, team, x, z, n, o),
        spawnSquad: (team, x, z, n, o) => spawnKnot(w, team, x, z, n, o),
        spawnOfficer: (team, x, z, n, o) => spawnOfficer(w, team, x, z, n, o),
        sendWave: (tx, tz, team, n) => sendWave(w, tx, tz, team, n),
        despawnFar: (need) => cullFar(w, need),
        get pressure() { return pressure; },
        set pressure(v) { pressure = clamp01(v); },
        get population() { return w.entities.stats.total; },
        get cap() { return cap; },
        setCap(n) { cap = Math.max(24, n | 0); return cap; },
        get hotspots() { return hot; },
        stats,
        resetBattle,
        reseed: resetBattle,
      };
      w.director = api;
    },

    fixedUpdate(w, dt) {
      if (!seeded) {
        if (!w.ai || !w.terrain) return;               // both init later than us
        seed(w);
        return;
      }
      const e = w.entities, pid = w.playerId;

      // ---- how hard is the player being pressed? ----------------------
      killT += dt;
      if (killT > 2) {
        const k = (w.player?.kills || 0);
        killRate = (k - killsAt) / killT * 60;         // KO per minute
        killsAt = k; killT = 0;
        const hpF = w.player && w.player.maxHp ? clamp01(w.player.hp / w.player.maxHp) : 1;
        const want = 0.18 + 0.42 * hpF + 0.40 * clamp01(killRate / 55);
        pressure += (want - pressure) * 0.35;
      }

      streamT -= dt;
      if (streamT > 0) return;
      streamT = 0.42;

      const pop = e.stats.total;
      if (pop > cap) cullFar(w, pop - cap);

      if (pid >= 0) {
        const near = e.countIn(e.x[pid], e.z[pid], 22, TEAM.ENEMY);
        const target = Math.round(5 + pressure * 20);
        stats.near = near; stats.target = target;
        if (near < target * 0.7) {
          // first try to pull an existing squad in — cheaper and it looks like
          // a battlefield reacting rather than units materialising
          const pulled = w.ai?.callToBattle?.(e.x[pid], e.z[pid], TEAM.ENEMY, 130, 2) || 0;
          if (pulled < 2 && pop < cap - 10) sendWave(w, e.x[pid], e.z[pid], TEAM.ENEMY, 8 + ((target - near) >> 1));
        }
      }
      if (pop < cap - 8) refill(w);
    },
  };
}
